"use client";

/**
 * Content-idea popup — asks readers what we should write next.
 *
 *  Scope is an ALLOWLIST, never a blocklist. With a blocklist every new page
 *  gets the popup by default and someone has to remember to exclude it; with an
 *  allowlist it only ever appears where we decided it should. It fires on
 *  reading pages — blog posts, guides, use-case spokes — and nowhere else.
 *
 *  Two routes are excluded despite matching the allowlist prefix:
 *  /guides/claude-plans-malaysia/ already carries the email-gated plan download,
 *  and it is the site's biggest page — two asks on one page and both lose. The
 *  cost calculator is mid-conversion for the same reason.
 *
 *  Trigger is 70% scroll AND 45s dwell, both required. Not on load, not on a
 *  timer alone, and never exit-intent. Google acts on interstitials that cover
 *  main content shortly after a search arrival, and the usual mobile
 *  exit-intent substitute fires on scroll-up — precisely the penalised pattern.
 *  By 70% the content has been read, so this is not that.
 *
 *  Email is optional by design. Required email turns a cheap question into a
 *  sixth lead form; optional costs nothing for people who skip it and gives us
 *  someone to tell when the thing they asked for actually ships.
 */

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import MarketingConsent from "@/components/forms/MarketingConsent";
import {
  appendMarketingConsent,
  withMarketingConsentLine,
} from "@/lib/marketing-consent";

/** Reading routes only. Prefix match. */
const ALLOW_PREFIXES = ["/blog/", "/guides/", "/solutions/agent-use-cases/"];

/** Wins over the allowlist — an active conversion already lives here. */
const DENY_EXACT_OR_PREFIX = [
  "/guides/claude-plans-malaysia/",
  "/tools/claude-cost-calculator/",
];

const STORAGE_KEY = "as_content_idea_v1";
const SESSION_KEY = "as_content_idea_seen";
const DISMISS_DAYS = 90;
const SCROLL_THRESHOLD = 0.7;
const DWELL_MS = 45_000;

const CHIPS = [
  "Real client case studies",
  "Agent build walkthroughs",
  "Prompt & workflow templates",
  "Team training kits",
  "Cost benchmarks",
  "Video walkthroughs",
];

/** Strips the /ms or /zh prefix so one allowlist covers every locale. */
function normalisePath(path: string): string {
  const stripped = path.replace(/^\/(ms|zh)(?=\/|$)/, "");
  const p = stripped === "" ? "/" : stripped;
  return p.endsWith("/") ? p : `${p}/`;
}

function isEligible(path: string): boolean {
  const p = normalisePath(path);
  if (p === "/blog/") return false; // the index — nothing has been read yet
  if (DENY_EXACT_OR_PREFIX.some((d) => p.startsWith(d))) return false;
  return ALLOW_PREFIXES.some((a) => p.startsWith(a));
}

/** Suppressed if submitted ever, or dismissed inside the cooling-off window. */
function isSuppressed(): boolean {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return true;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw) as { state?: string; at?: number };
    if (saved.state === "submitted") return true;
    if (saved.state === "dismissed" && saved.at) {
      return Date.now() - saved.at < DISMISS_DAYS * 86_400_000;
    }
  } catch {
    // Private mode or storage disabled — fail closed rather than nag.
    return true;
  }
  return false;
}

function remember(state: "submitted" | "dismissed") {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* nothing useful to do */
  }
}

export default function ContentIdeaPopup() {
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  useEffect(() => {
    if (typeof window === "undefined") return;

    // ?popup=test — skips the 45s dwell, the scroll threshold and the storage
    // suppression so any page can be checked instantly. The route allowlist is
    // deliberately NOT bypassed: the useful test is confirming it appears on a
    // blog post and stays away from /guides/claude-plans-malaysia/ and /contact/,
    // and bypassing the allowlist would make that test meaningless. Harmless
    // without the parameter, so it can live in production.
    const isTest = new URLSearchParams(window.location.search).get("popup") === "test";

    if (!isEligible(window.location.pathname)) return;
    if (!isTest && isSuppressed()) return;

    if (isTest) {
      setOpen(true);
      return;
    }

    let dwellMet = false;
    let scrollMet = false;
    const maybeOpen = () => {
      if (dwellMet && scrollMet) {
        setOpen(true);
        trackEvent("content_idea_prompt_shown", { category: "engagement" });
        cleanup();
      }
    };
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= SCROLL_THRESHOLD) {
        scrollMet = true;
        maybeOpen();
      }
    };
    const timer = window.setTimeout(() => {
      dwellMet = true;
      maybeOpen();
    }, DWELL_MS);
    function cleanup() {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return cleanup;
  }, []);

  const close = useCallback((reason: "dismissed" | "submitted") => {
    // Test mode leaves no trace, so a page can be checked repeatedly.
    const isTest =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("popup") === "test";
    if (!isTest) remember(reason);
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close("dismissed");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  const toggle = (chip: string) =>
    setPicked((prev) =>
      prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip],
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!picked.length && !note.trim()) return;
    setStatus("loading");

    const key = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    if (!key || key === "your_actual_web3forms_key_here") {
      setStatus("error");
      return;
    }
    try {
      const body = new FormData();
      body.append("access_key", key);
      body.append("request_type", "Content idea");
      body.append("subject", "Content idea from the website");
      body.append("from_name", "Anchor Sprint Website");
      body.append("wants", picked.join(", ") || "(none selected)");
      body.append(
        "message",
        withMarketingConsentLine(
          note.trim() || "(no free-text note)",
          marketingConsent,
        ),
      );
      if (email.trim()) body.append("email", email.trim());
      body.append("page", window.location.pathname);
      appendMarketingConsent(body, marketingConsent, locale);

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "failed");

      // NON-key event on purpose. Adding this to the canonical six lead events
      // would lift the baseline lead rate for a reason unrelated to leads.
      trackEvent("content_idea_submit", {
        category: "engagement",
        label: picked.join("|") || "freetext_only",
        // Whether an email was given, never the address itself.
        with_email: email.trim() ? "yes" : "no",
      });
      setStatus("done");
      window.setTimeout(() => close("submitted"), 1600);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="content-idea-title"
      onClick={() => close("dismissed")}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl motion-safe:animate-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => close("dismissed")}
          aria-label={t(locale, "Close")}
          className="absolute right-4 top-4 text-neutral-muted transition-colors hover:text-neutral-emphasis"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        {status === "done" ? (
          <p className="py-8 text-center text-base font-medium text-neutral-emphasis">
            {t(locale, "Thank you — that genuinely shapes what we write next.")}
          </p>
        ) : (
          <form onSubmit={submit}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              {t(locale, "Help us improve")}
            </p>
            <h2
              id="content-idea-title"
              className="font-display text-xl font-bold leading-snug text-neutral-emphasis"
            >
              {t(locale, "What would you like us to write next?")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-neutral-muted">
              {t(locale, "Pick anything useful. It takes a few seconds and we do read every one.")}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => toggle(chip)}
                  aria-pressed={picked.includes(chip)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    picked.includes(chip)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-neutral-muted hover:border-primary/40"
                  }`}
                >
                  {t(locale, chip)}
                </button>
              ))}
            </div>

            <label className="mt-5 block text-sm font-medium text-neutral-emphasis">
              {t(locale, "Anything else?")}
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={t(locale, "A topic, tool or page that would help you…")}
                className="mt-1.5 w-full rounded-lg border border-border bg-background p-3 text-sm text-neutral-emphasis placeholder:text-neutral-muted focus:border-primary focus:outline-none"
              />
            </label>

            <label className="mt-4 block text-sm font-medium text-neutral-emphasis">
              {t(locale, "Email (optional)")}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t(locale, "Only if you want us to tell you when we publish it")}
                className="mt-1.5 w-full rounded-lg border border-border bg-background p-3 text-sm text-neutral-emphasis placeholder:text-neutral-muted focus:border-primary focus:outline-none"
              />
            </label>

            <MarketingConsent
              id="content-idea-marketing-consent"
              checked={marketingConsent}
              onChange={setMarketingConsent}
              locale={locale}
              className="mt-4"
            />

            {status === "error" && (
              <p className="mt-3 text-sm text-destructive">
                {t(locale, "That didn't send. Please try again shortly.")}
              </p>
            )}

            <div className="mt-6 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => close("dismissed")}
                className="text-sm text-neutral-muted transition-colors hover:text-neutral-emphasis"
              >
                {t(locale, "Maybe later")}
              </button>
              <button
                type="submit"
                disabled={status === "loading" || (!picked.length && !note.trim())}
                className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === "loading" ? t(locale, "Sending…") : t(locale, "Send ideas")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
